import type { TranslationKey } from '@/i18n-key';
import type { ReactNode } from 'react';

export interface ProficiencyIconEntry {
    value: string;
    labelKey: TranslationKey;
    colorClass: string;
    paths: ReactNode;
}

const stroke = {
    fill: 'none' as const,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 2,
};

export const PROFICIENCY_ICON_CATALOG: ProficiencyIconEntry[] = [
    {
        value: 'achieved',
        labelKey: 'competencies.levels.icons.achieved',
        colorClass: 'text-[#22c55e]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M22 11.08V12a10 10 0 1 1-5.93-9.14'}
                />

                <path
                    {...stroke}
                    d={'M22 4 12 14.01l-3-3'}
                />
            </>
        ),
    },
    {
        value: 'almostAchieved',
        labelKey: 'competencies.levels.icons.almostAchieved',
        colorClass: 'text-[#14b8a6]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M23 6l-9.5 9.5-5-5L1 18'}
                />

                <path
                    {...stroke}
                    d={'M17 6h6v6'}
                />
            </>
        ),
    },
    {
        value: 'evolution',
        labelKey: 'competencies.levels.icons.evolution',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M23 4v6h-6'}
                />

                <path
                    {...stroke}
                    d={'M1 20v-6h6'}
                />

                <path
                    {...stroke}
                    d={'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'}
                />
            </>
        ),
    },
    {
        value: 'notMet',
        labelKey: 'competencies.levels.icons.notMet',
        colorClass: 'text-[#ef4444]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'10'}
                />

                <path
                    {...stroke}
                    d={'M8 12h8'}
                />
            </>
        ),
    },
    {
        value: 'star',
        labelKey: 'competencies.levels.icons.star',
        colorClass: 'text-purple',
        paths: (
            <path
                {...stroke}
                d={
                    'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
                }
            />
        ),
    },
    {
        value: 'target',
        labelKey: 'competencies.levels.icons.target',
        colorClass: 'text-primary',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'10'}
                />

                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'6'}
                />

                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'2'}
                />
            </>
        ),
    },
    {
        value: 'award',
        labelKey: 'competencies.levels.icons.award',
        colorClass: 'text-[#3b82f6]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'8'}
                    r={'7'}
                />

                <path
                    {...stroke}
                    d={'M8.21 13.89 7 23l5-3 5 3-1.21-9.12'}
                />
            </>
        ),
    },
    {
        value: 'bolt',
        labelKey: 'competencies.levels.icons.bolt',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <path
                {...stroke}
                d={'M13 2 3 14h9l-1 8 10-12h-9l1-8z'}
            />
        ),
    },
    {
        value: 'trophy',
        labelKey: 'competencies.levels.icons.trophy',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M6 9H4.5a2.5 2.5 0 0 1 0-5H6'}
                />

                <path
                    {...stroke}
                    d={'M18 9h1.5a2.5 2.5 0 0 0 0-5H18'}
                />

                <path
                    {...stroke}
                    d={'M4 22h16'}
                />

                <path
                    {...stroke}
                    d={'M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22'}
                />

                <path
                    {...stroke}
                    d={'M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22'}
                />

                <path
                    {...stroke}
                    d={'M18 2H6v7a6 6 0 0 0 12 0V2z'}
                />
            </>
        ),
    },
    {
        value: 'medal',
        labelKey: 'competencies.levels.icons.medal',
        colorClass: 'text-[#d97706]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={
                        'M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15'
                    }
                />

                <path
                    {...stroke}
                    d={'M11 12 5.12 2.2'}
                />

                <path
                    {...stroke}
                    d={'m13 12 5.88-9.8'}
                />

                <path
                    {...stroke}
                    d={'M8 7h8'}
                />

                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'17'}
                    r={'5'}
                />

                <path
                    {...stroke}
                    d={'M12 18v-2h-.5'}
                />
            </>
        ),
    },
    {
        value: 'crown',
        labelKey: 'competencies.levels.icons.crown',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <path
                {...stroke}
                d={'M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14'}
            />
        ),
    },
    {
        value: 'flag',
        labelKey: 'competencies.levels.icons.flag',
        colorClass: 'text-primary',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z'}
                />

                <path
                    {...stroke}
                    d={'M4 22v-7'}
                />
            </>
        ),
    },
    {
        value: 'sparkles',
        labelKey: 'competencies.levels.icons.sparkles',
        colorClass: 'text-purple',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M9.94 14.06 12 21l2.06-6.94L21 12l-6.94-2.06L12 3 9.94 9.94 3 12z'}
                />

                <path
                    {...stroke}
                    d={'M19 3v4'}
                />

                <path
                    {...stroke}
                    d={'M21 5h-4'}
                />
            </>
        ),
    },
    {
        value: 'thumbsUp',
        labelKey: 'competencies.levels.icons.thumbsUp',
        colorClass: 'text-[#22c55e]',
        paths: (
            <path
                {...stroke}
                d={
                    'M7 11v10H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zm0 0 4.6-7.6A2 2 0 0 1 15 4.8V8h4.3a2 2 0 0 1 2 2.3l-1.4 8A2 2 0 0 1 18 20H7'
                }
            />
        ),
    },
    {
        value: 'trendingUp',
        labelKey: 'competencies.levels.icons.trendingUp',
        colorClass: 'text-[#22c55e]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M22 7 13.5 15.5 8.5 10.5 2 17'}
                />

                <path
                    {...stroke}
                    d={'M16 7h6v6'}
                />
            </>
        ),
    },
    {
        value: 'checkCheck',
        labelKey: 'competencies.levels.icons.checkCheck',
        colorClass: 'text-[#22c55e]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M18 6 7 17l-5-5'}
                />

                <path
                    {...stroke}
                    d={'M22 10 13.5 18.5 12 17'}
                />
            </>
        ),
    },
    {
        value: 'rocket',
        labelKey: 'competencies.levels.icons.rocket',
        colorClass: 'text-purple',
        paths: (
            <>
                <path
                    {...stroke}
                    d={
                        'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z'
                    }
                />

                <path
                    {...stroke}
                    d={
                        'M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z'
                    }
                />

                <path
                    {...stroke}
                    d={'M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0'}
                />

                <path
                    {...stroke}
                    d={'M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5'}
                />
            </>
        ),
    },
    {
        value: 'lightbulb',
        labelKey: 'competencies.levels.icons.lightbulb',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={
                        'M15 14c.2-1 .7-1.7 1.5-2.5C17.7 10.2 18 9 18 8a6 6 0 0 0-12 0c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'
                    }
                />

                <path
                    {...stroke}
                    d={'M9 18h6'}
                />

                <path
                    {...stroke}
                    d={'M10 22h4'}
                />
            </>
        ),
    },
    {
        value: 'graduationCap',
        labelKey: 'competencies.levels.icons.graduationCap',
        colorClass: 'text-primary',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M22 10 12 5 2 10l10 5 10-5z'}
                />

                <path
                    {...stroke}
                    d={'M6 12v5c0 1 2.5 3 6 3s6-2 6-3v-5'}
                />

                <path
                    {...stroke}
                    d={'M22 10v6'}
                />
            </>
        ),
    },
    {
        value: 'gauge',
        labelKey: 'competencies.levels.icons.gauge',
        colorClass: 'text-primary',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M12 14 16 9'}
                />

                <path
                    {...stroke}
                    d={'M3.34 19a10 10 0 1 1 17.32 0'}
                />
            </>
        ),
    },
    {
        value: 'barChart',
        labelKey: 'competencies.levels.icons.barChart',
        colorClass: 'text-[#3b82f6]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M3 3v18h18'}
                />

                <path
                    {...stroke}
                    d={'M7 16v-4'}
                />

                <path
                    {...stroke}
                    d={'M12 16V8'}
                />

                <path
                    {...stroke}
                    d={'M17 16v-7'}
                />
            </>
        ),
    },
    {
        value: 'podium',
        labelKey: 'competencies.levels.icons.podium',
        colorClass: 'text-[#f59e0b]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M9 21V11h6v10'}
                />

                <path
                    {...stroke}
                    d={'M3 21v-6h6'}
                />

                <path
                    {...stroke}
                    d={'M15 21v-8h6v8'}
                />

                <path
                    {...stroke}
                    d={'M3 21h18'}
                />
            </>
        ),
    },
    {
        value: 'ribbon',
        labelKey: 'competencies.levels.icons.ribbon',
        colorClass: 'text-[#E64980]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'9'}
                    r={'6'}
                />

                <path
                    {...stroke}
                    d={'M9 14.5 6 22l6-3 6 3-3-7.5'}
                />
            </>
        ),
    },
    {
        value: 'certificate',
        labelKey: 'competencies.levels.icons.certificate',
        colorClass: 'text-[#3b82f6]',
        paths: (
            <>
                <rect
                    {...stroke}
                    x={'3'}
                    y={'3'}
                    width={'18'}
                    height={'14'}
                    rx={'2'}
                />

                <path
                    {...stroke}
                    d={'M7 8h6'}
                />

                <path
                    {...stroke}
                    d={'M7 12h4'}
                />

                <circle
                    {...stroke}
                    cx={'17'}
                    cy={'18'}
                    r={'3'}
                />

                <path
                    {...stroke}
                    d={'M15.5 20.5 14 23l3-1.2L20 23l-1.5-2.5'}
                />
            </>
        ),
    },
    {
        value: 'key',
        labelKey: 'competencies.levels.icons.key',
        colorClass: 'text-[#d97706]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'7.5'}
                    cy={'15.5'}
                    r={'5.5'}
                />

                <path
                    {...stroke}
                    d={'m21 2-9.6 9.6'}
                />

                <path
                    {...stroke}
                    d={'m15.5 7.5 3 3L22 7l-3-3'}
                />
            </>
        ),
    },
    {
        value: 'compass',
        labelKey: 'competencies.levels.icons.compass',
        colorClass: 'text-primary',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'10'}
                />

                <path
                    {...stroke}
                    d={'m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z'}
                />
            </>
        ),
    },
    {
        value: 'summit',
        labelKey: 'competencies.levels.icons.summit',
        colorClass: 'text-purple',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'m8 3 4 8 5-5 5 15H2L8 3z'}
                />

                <path
                    {...stroke}
                    d={'m6.5 12 2.5-2 2 2'}
                />
            </>
        ),
    },
    {
        value: 'seedling',
        labelKey: 'competencies.levels.icons.seedling',
        colorClass: 'text-[#22c55e]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M12 22V11'}
                />

                <path
                    {...stroke}
                    d={'M12 11C12 7 9 4 4 4c0 4 3 7 8 7z'}
                />

                <path
                    {...stroke}
                    d={'M12 14c0-3 2.5-5.5 6.5-5.5C18.5 12 16 14 12 14z'}
                />
            </>
        ),
    },
    {
        value: 'shieldCheck',
        labelKey: 'competencies.levels.icons.shieldCheck',
        colorClass: 'text-[#22c55e]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'}
                />

                <path
                    {...stroke}
                    d={'m9 12 2 2 4-4'}
                />
            </>
        ),
    },
    {
        value: 'checkCircle',
        labelKey: 'competencies.levels.icons.checkCircle',
        colorClass: 'text-[#0ea5e9]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'10'}
                />

                <path
                    {...stroke}
                    d={'m9 12 2 2 4-4'}
                />
            </>
        ),
    },
    {
        value: 'alert',
        labelKey: 'competencies.levels.icons.alert',
        colorClass: 'text-[#f97316]',
        paths: (
            <>
                <path
                    {...stroke}
                    d={
                        'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'
                    }
                />

                <line
                    {...stroke}
                    x1={'12'}
                    y1={'9'}
                    x2={'12'}
                    y2={'13'}
                />

                <line
                    {...stroke}
                    x1={'12'}
                    y1={'17'}
                    x2={'12.01'}
                    y2={'17'}
                />
            </>
        ),
    },
    {
        value: 'warning',
        labelKey: 'competencies.levels.icons.warning',
        colorClass: 'text-[#ef4444]',
        paths: (
            <>
                <circle
                    {...stroke}
                    cx={'12'}
                    cy={'12'}
                    r={'10'}
                />

                <line
                    {...stroke}
                    x1={'12'}
                    y1={'8'}
                    x2={'12'}
                    y2={'12'}
                />

                <line
                    {...stroke}
                    x1={'12'}
                    y1={'16'}
                    x2={'12.01'}
                    y2={'16'}
                />
            </>
        ),
    },
];

export const getIconEntry = (value: string): ProficiencyIconEntry | undefined => {
    return PROFICIENCY_ICON_CATALOG.find((entry) => entry.value === value);
};
